import { Link } from 'react-router-dom'

const HERO_SRC = '/studios-misr.jpg'

export default function Landing() {
  return (
    <main className="bg-ink-900 text-ink-50">
      {/* HERO ----------------------------------------------------------------- */}
      <section className="relative min-h-screen overflow-hidden">
        {/* Background photo */}
        <div className="absolute inset-0">
          <img
            src={HERO_SRC}
            alt="استوديو مصر، 1935"
            className="absolute inset-0 w-full h-full object-cover effeh-hero-pan"
            loading="eager"
          />
          {/* Heavy bottom-weighted gradient so the headline + CTA read */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(14, 14, 14, 0.55) 0%, rgba(14, 14, 14, 0.35) 35%, rgba(14, 14, 14, 0.72) 72%, rgba(14, 14, 14, 0.94) 100%)',
            }}
          />
          {/* Subtle vignette */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse at center, transparent 30%, rgba(14, 14, 14, 0.55) 100%)',
            }}
          />
          {/* Film grain */}
          <div className="absolute inset-0 effeh-grain opacity-20 mix-blend-overlay pointer-events-none" />
        </div>

        {/* Top nav */}
        <header className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-6 sm:pt-8 flex items-center justify-between text-ink-100/85">
          <div className="flex items-baseline gap-3 text-xs uppercase tracking-[0.3em]">
            <span aria-hidden className="inline-block w-6 effeh-rule h-px" />
            <span>سينما مصر</span>
          </div>
          <div className="text-xs tracking-[0.25em] uppercase text-ink-100/65">
            لعبة يومية
          </div>
        </header>

        {/* Hero content */}
        <div className="relative z-10 max-w-3xl mx-auto px-6 pt-24 sm:pt-32 pb-24 text-center">
          <h1 className="font-display font-bold tracking-tight leading-[0.95] text-[clamp(5rem,14vw,9rem)] text-amber-300 drop-shadow-[0_2px_24px_rgba(0,0,0,0.7)]">
            إفيه
          </h1>

          <div className="mx-auto mt-6 sm:mt-8 w-32 effeh-rule" />

          <p className="font-display italic mt-6 sm:mt-8 text-xl sm:text-3xl leading-snug text-ink-50 drop-shadow-[0_1px_8px_rgba(0,0,0,0.6)]">
            اقرأ الإفيه. خمّن الفيلم. خمّن مين قاله.
          </p>

          <p className="mt-6 sm:mt-8 text-base sm:text-lg leading-relaxed text-ink-100/90 max-w-2xl mx-auto drop-shadow-[0_1px_6px_rgba(0,0,0,0.5)]">
            جُمل السينما المصرية اللي بقت في كلامنا اليومي. اقرأ الإفيه،
            خمّن الفيلم والممثل، وكل ما تغلط حتطلع لك تلميحة جديدة.
            ٥ إفيهات في كل جولة. والعب براحتك.
          </p>

          <div className="mt-10 sm:mt-12 flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/play"
              className="inline-block bg-amber-500 text-ink-900 rounded-md px-10 sm:px-12 py-3.5 sm:py-4 text-base sm:text-lg font-bold tracking-wide hover:bg-amber-300 transition shadow-[0_6px_28px_rgba(0,0,0,0.5)] hover:shadow-[0_8px_36px_rgba(0,0,0,0.6)] hover:-translate-y-0.5 duration-200"
            >
              ابدأ جولة (٥ إفيهات)
            </Link>
            <Link
              to="/daily"
              className="inline-block border border-amber-500/50 text-amber-300 rounded-md px-7 sm:px-9 py-3.5 sm:py-4 text-base sm:text-lg font-bold tracking-wide hover:bg-amber-500/10 transition"
            >
              إفيه اليوم
            </Link>
          </div>

          <p className="mt-8 text-[11px] tracking-[0.3em] uppercase text-ink-100/65">
            {new Date().toLocaleDateString('ar-EG', {
              year: 'numeric', month: 'long', day: 'numeric',
            })}
          </p>
        </div>

        {/* Photo credit */}
        <div className="absolute bottom-3 left-3 text-[10px] text-ink-100/40 tracking-wider z-10" dir="ltr">
          Studio Misr · est. 1935 · Cairo
        </div>
      </section>

      {/* HOW IT WORKS --------------------------------------------------------- */}
      <section className="bg-ink-900 py-20 sm:py-28 px-6 border-t border-ink-700/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14 sm:mb-20">
            <div className="mx-auto w-24 effeh-rule mb-6" />
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-ink-50">
              إزاي تلعب؟
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-10 sm:gap-14">
            {HOW.map((step, i) => (
              <div key={step.title} className="text-start">
                <div className="font-display text-3xl text-amber-500 mb-2 tabular-nums">
                  ٠{i + 1}
                </div>
                <div className="w-8 effeh-rule mb-4" />
                <h3 className="font-display text-2xl sm:text-[1.7rem] mb-3 leading-tight text-ink-50">
                  {step.title}
                </h3>
                <p className="text-ink-100/80 leading-relaxed text-[1.05rem]">
                  {step.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EXAMPLE EFFEH -------------------------------------------------------- */}
      <section className="bg-ink-700/30 py-20 sm:py-28 px-6 border-y border-ink-700/50">
        <div className="max-w-3xl mx-auto text-center">
          <div className="mx-auto w-24 effeh-rule mb-6" />
          <p className="text-xs uppercase tracking-[0.3em] text-amber-500 mb-6">مثال</p>
          <p className="effeh-quote font-display text-3xl sm:text-5xl leading-relaxed text-ink-50">
            اشمعنى أنا
          </p>
          <p className="mt-8 text-ink-100/70 text-sm">
            عرفت من قاله؟ ومن أنهي فيلم؟ تعالى جرب.
          </p>
          <div className="mt-10">
            <Link
              to="/play"
              className="inline-block bg-amber-500 text-ink-900 rounded-md px-9 py-3 text-base font-bold tracking-wide hover:bg-amber-300 transition"
            >
              ابدأ جولة
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER --------------------------------------------------------------- */}
      <footer className="py-10 px-6 text-center text-xs text-ink-500">
        <div className="mx-auto w-12 effeh-rule mb-4" />
        نسخة تجريبية · made with 💛 in Cairo
      </footer>
    </main>
  )
}

const HOW = [
  {
    title: 'الجولة ٥ إفيهات',
    body: 'كل جولة ٥ إفيهات من السينما المصرية. جُمل بقت معاك في الكلام اليومي. خمّن اسم الفيلم ومين قاله.',
  },
  {
    title: 'كل غلطة، تلميحة',
    body: '٥ محاولات للإفيه الواحد. كل ما تغلط حتطلع لك تلميحة جديدة: السنة، نوع العمل، اسم الشخصية، أول حرف، وأكتر.',
  },
  {
    title: 'العب براحتك',
    body: 'خلصت الجولة؟ ابدأ جولة تانية على طول. النقط بتتجمع، وأفضل جولة بتفضل محفوظة. مفيش حد بيوقفك.',
  },
]
